<template>
    <AppModal ref="modal" :title="$t('labels.copy_products')" @on-hide="hide" size="full">
        <div class="modal-body">
            <template v-if="fetchTask.state.isRunning">
                <TaskProgressBar :done="fetchTask.state.done" :total="fetchTask.state.total"/>
            </template>
            <template v-else-if="!fetchingFromDestinationIntegrationIsCompleted && (destinationIntegrations.length === 0 || !fetchTask.state.isRunning)">
                <p>{{ $t('labels.copy_products_description') }}</p>
                <ul class="list">
                    <li>{{ $t('labels.copy_products_instruction_1') }}</li>
                    <li>{{ $t('labels.copy_products_instruction_2') }}</li>
                </ul>
                <p>{{ $t('labels.copy_products_logic_title') }}</p>
                <ul>
                    <li>{{ $t('labels.copy_products_logic_1') }}</li>
                    <li>{{ $t('labels.copy_products_logic_2') }}</li>
                </ul>
                <FormGroup :label="$t('labels.integration')" required>
                    <MultiAutoComplete
                        :url="`/projects/${$route.params.projectUuid}/pim/channels`"
                        :model-value="destinationIntegrations"
                        :query-params="{
                            ignore_uuids: [this.$route.params.integrationUuid],
                            exclude_suppliers: 1,
                        }"
                        @update:model-value="integrations => destinationIntegrations = integrations"
                    />
                </FormGroup>
                
                <template v-for="integration in destinationIntegrations" :key="integration.uuid">
                    <FormGroup :label="`${$t('labels.category')} (${integration.name})`">
                        <AppAutoComplete
                            :url="`/projects/${$route.params.projectUuid}/pim/channels/${integration.uuid}/categories`"
                            :model-value="destinationCategories[integration.uuid]?.uuid"
                            :selected-value="destinationCategories[integration.uuid]?.name"
                            @update:model-value="category => setDestinationCategory(integration.uuid, category)"
                            can-be-empty
                        />
                    </FormGroup>
                </template>
            </template>
            <template v-else-if="copyTask.state.isRunning">
                <TaskProgressBar :done="copyTask.state.done" :total="copyTask.state.total"/>
            </template>
            <template v-else-if="copyBadHttpHistory.length > 0">
                <div class="alert alert-warning border-0 alert-dismissible mt-3">
                    <button type="button" class="close" data-dismiss="alert"><span>×</span></button>
                    <span class="font-weight-semibold">{{ $t('labels.copy_products_warning_title') }}</span>
                    <p>{{ $t('labels.copy_products_warning_message') }}</p>
                    <p>{{ $t('labels.copy_products_warning_manual') }}</p>
                    <p>{{ $t('labels.copy_products_error_details') }}</p>
                </div>
                <div class="table-responsive table-scrollable">
                    <table class="table table-bordered table-fixed">
                        <thead>
                            <tr>
                                <th>{{ $t('labels.copy_products_request') }}</th>
                                <th>{{ $t('labels.copy_products_response') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(badHttpHistoryItem, index) in copyBadHttpHistory" :key="index">
                                <td><span class="text-nowrap">{{ badHttpHistoryItem.request.body }}</span></td>
                                <td><span class="text-nowrap">{{ badHttpHistoryItem.response.body }}</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
            <template v-else>
                <p>{{ $t('labels.copy_products_table_title') }}</p>
                <p>{{ $t('labels.copy_products_table_description') }}</p>
                <p>{{ $t('labels.copy_products_table_columns_title') }}</p>
                <ul class="list">
                    <li>{{ $t('labels.copy_products_table_create') }}</li>
                    <li>{{ $t('labels.copy_products_table_update') }}</li>
                    <li>{{ $t('labels.copy_products_table_force_update') }}</li>
                </ul>
                <div class="alert alert-info border-0 alert-dismissible" v-if="everyProductHasDestinationProduct">
                    <button type="button" class="close" data-dismiss="alert"><span>×</span></button>
                    {{ $t('labels.copy_products_alert_all_exist') }}
                </div>
                <div class="alert alert-info border-0 alert-dismissible" v-if="everyProductHasntDestinationProduct">
                    <button type="button" class="close" data-dismiss="alert"><span>×</span></button>
                    {{ $t('labels.copy_products_alert_none_exist') }}
                </div>
                <div class="alert alert-info border-0" v-if="!someDestinationCategorySelected">
                    {{ $t('labels.copy_products_alert_no_category') }}
                </div>
                <template v-if="multipleDestinationRows.length > 0">
                    <p class="font-weight-semibold mb-1">{{ $t('labels.copy_products_multiple_title') }}</p>
                    <p>{{ $t('labels.copy_products_multiple_description') }}</p>
                    <div class="alert alert-warning border-0" v-if="unresolvedDestinationRowsCount > 0">
                        {{ $t('labels.copy_products_multiple_unresolved', {total: unresolvedDestinationRowsCount}) }}
                    </div>
                    <div class="table-responsive mb-3">
                        <table class="table table-bordered">
                            <thead>
                                <tr>
                                    <th>{{ $t('labels.copy_products_channel_label') }}</th>
                                    <th>{{ $t('labels.copy_products_source_publication_label') }}</th>
                                    <th>{{ $t('labels.copy_products_destination_publications_label') }}</th>
                                    <th>
                                        <div class="d-flex align-items-center">
                                            <AppCheckbox
                                                class="mr-2"
                                                :model-value="allDestinationTargetsSelected"
                                                @update:model-value="setAllDestinationTargets"
                                            />
                                            {{ $t('labels.copy_products_update_label') }}
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="row in multipleDestinationRows" :key="row.key">
                                    <tr v-for="(destination, index) in row.destinations" :key="destination.uuid">
                                        <td v-if="index === 0" :rowspan="row.destinations.length" class="align-middle">
                                            {{ row.integration.name }}
                                        </td>
                                        <td v-if="index === 0" :rowspan="row.destinations.length" class="align-middle">
                                            {{ publicationTitle(row.product) }}
                                            <br>
                                            <span class="text-muted">{{ row.product.sku }}</span>
                                        </td>
                                        <td>
                                            <router-link
                                                target="_blank"
                                                :to="{name: 'Product', params: {integrationUuid: row.integration.uuid, integrationProductUuid: destination.uuid}}"
                                            >
                                                {{ publicationTitle(destination) }}
                                            </router-link>
                                            <br>
                                            <span class="text-muted">{{ destination.sku }}</span>
                                        </td>
                                        <td>
                                            <AppCheckbox
                                                :model-value="isDestinationTargetSelected(row, destination)"
                                                @update:model-value="checked => toggleDestinationTarget(row, destination, checked)"
                                            />
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </template>
                <div class="table-responsive">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th>{{ $t('labels.copy_products_field_label') }}</th>
                                <th>{{ $t('labels.copy_products_create_label') }}</th>
                                <th>{{ $t('labels.copy_products_update_label') }}</th>
                                <th>{{ $t('labels.copy_products_force_update_label') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th>{{ $t('labels.copy_products_select_all') }}</th>
                                <th>
                                    <AppCheckbox
                                        type="checkbox"
                                        :model-value="allFieldsSelectedForCreate"
                                        @update:model-value="checkAllFields('create', $event)"
                                        :disabled="everyProductHasDestinationProduct || !someDestinationCategorySelected"
                                    />
                                </th>
                                <th>
                                    <AppCheckbox
                                        type="checkbox"
                                        :model-value="allFieldsSelectedForUpdate"
                                        @update:model-value="checkAllFields('update', $event)"
                                        :disabled="everyProductHasntDestinationProduct"
                                    />
                                </th>
                                <th>
                                    <AppCheckbox
                                        type="checkbox"
                                        :model-value="allFieldsSelectedForForceUpdate"
                                        @update:model-value="checkAllFields('forceUpdate', $event)"
                                        :disabled="everyProductHasntDestinationProduct"
                                    />
                                </th>
                            </tr>
                            <tr v-for="(field, index) in fields" :key="index">
                                <td>{{ field.title }}</td>
                                <td>
                                    <AppCheckbox
                                        type="checkbox"
                                        v-model="field.create"
                                        :disabled="everyProductHasDestinationProduct || !someDestinationCategorySelected || requiredFieldsForCreate.includes(field.key)"
                                    />
                                </td>
                                <td>
                                    <AppCheckbox
                                        type="checkbox"
                                        v-model="field.update"
                                        :disabled="everyProductHasntDestinationProduct"
                                    />
                                </td>
                                <td>
                                    <AppCheckbox
                                        type="checkbox"
                                        v-model="field.forceUpdate"
                                        :disabled="everyProductHasntDestinationProduct"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </div>
        <div class="modal-footer" v-if="!fetchTask.state.isRunning && !copyTask.state.isRunning">
            <AppButton color="indigo" @click="getProductsData" v-if="!fetchingFromDestinationIntegrationIsCompleted" :disabled="destinationIntegrations.length === 0">
                {{ $t('buttons.continue') }}
            </AppButton>
            <AppButton color="indigo" @click="copyProductsToDestination" v-if="fetchingFromDestinationIntegrationIsCompleted">
                {{ $t('buttons.copy') }}
            </AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppModal from '../../../../components/new/modal/AppModal.vue';
import AppButton from '../../../../components/new/button/AppButton.vue';
import FormGroup from '../../../../components/new/form/group/FormGroup.vue';
import AppAutoComplete from '../../../../components/new/form/autocomplete/AppAutoComplete.vue';
import MultiAutoComplete from '../../../../components/new/form/autocomplete/MultiAutoComplete.vue';
import TaskProgressBar from '../../../../components/new/progress/TaskProgressBar.vue';
import AppCheckbox from '../../../../components/new/form/checkbox/AppCheckbox.vue';

import HttpRequest from '../../../../services/Http/HttpRequest';
import HttpSender from '../../../../services/Http/HttpSender';
import { chunk } from '../../../../utils';
import MessageNotificator from '../../../../services/MessageNotificator';
import { createProgressTask } from '../../../../services/Progress/ProgressTask';

export default {
    name: "CopyProductsModal",
    components: {
        AppModal, AppButton, FormGroup, AppAutoComplete, MultiAutoComplete, TaskProgressBar, AppCheckbox
    },
    data() {
        return {
            destinationCategories: {},
            destinationTargets: {},
            destinationIntegrations: [],
            currentIntegrationProductUuids: [],
            currentIntegrationProducts: [],
            fetchTask: createProgressTask(),
            copyTask: createProgressTask(),
            fetchingFromDestinationIntegrationIsCompleted: false,
            fields: [
                { key: 'source_id',         create: false, update: false, forceUpdate: false, title: this.$t('labels.source_id') },
                { key: 'second_source_id',  create: false, update: false, forceUpdate: false, title: this.$t('labels.second_source_id') },
                { key: 'sku',               create: false, update: false, forceUpdate: false, title: this.$t('labels.sku') },
                { key: 'names.uk',          create: true,  update: false, forceUpdate: false, title: `${this.$t('labels.name')} (${this.$t('languages.uk')})` },
                { key: 'names.ru',          create: true,  update: false, forceUpdate: false, title: `${this.$t('labels.name')} (${this.$t('languages.ru')})` },
                { key: 'descriptions.uk',   create: false, update: false, forceUpdate: false, title: `${this.$t('labels.description')} (${this.$t('languages.uk')})` },
                { key: 'descriptions.ru',   create: false, update: false, forceUpdate: false, title: `${this.$t('labels.description')} (${this.$t('languages.ru')})` },
                { key: 'seos.uk',           create: false, update: false, forceUpdate: false, title: `${this.$t('labels.seo')} (${this.$t('languages.uk')})` },
                { key: 'seos.ru',           create: false, update: false, forceUpdate: false, title: `${this.$t('labels.seo')} (${this.$t('languages.ru')})` },
                { key: 'images',            create: false, update: false, forceUpdate: false, title: this.$t('labels.images') },
                { key: 'group_key',         create: false, update: false, forceUpdate: false, title: this.$t('labels.group_key') },
                { key: 'manufacturer_name', create: false, update: false, forceUpdate: false, title: this.$t('labels.manufacturer_name') },
                { key: 'price',             create: false, update: false, forceUpdate: false, title: this.$t('labels.price') },
                { key: 'sale_price',        create: false, update: false, forceUpdate: false, title: this.$t('labels.sale_price') },
                { key: 'balance',           create: false, update: false, forceUpdate: false, title: this.$t('labels.balance') }
            ],
            copyBadHttpHistory: [],
            httpSender: new HttpSender(),
            requiredFieldsForCreate: ['names.uk', 'names.ru']
        };
    },
    methods: {
        show(productUuids) {
            this.currentIntegrationProductUuids = productUuids;
            this.$refs.modal.show();
        },
        hide() {
            this.fetchTask.cancel();
            this.copyTask.cancel();
            this.$refs.modal.hide();
            this.destinationIntegrations = [];
            this.destinationCategories = {};
            this.destinationTargets = {};
            this.currentIntegrationProductUuids = [];
            this.currentIntegrationProducts = [];
            this.fetchingFromDestinationIntegrationIsCompleted = false;
            this.copyBadHttpHistory = [];
            this.httpSender = new HttpSender(); 
            for (let field of ['create', 'update', 'forceUpdate']) {
                this.checkAllFields(field, false);
            }
        },
        async getProductsData() {
            const completed = await this.fetchTask.run(async (progress) => {
                const products = await this.collectCurrentIntegrationProducts(progress);

                if (!progress.signal.aborted) {
                    this.currentIntegrationProducts = products;
                }
            });

            if (completed) {
                this.fetchingFromDestinationIntegrationIsCompleted = true;
            }
        },
        async collectCurrentIntegrationProducts(progress) {
            let currentIntegrationProducts = await this.fetchIntegrationProducts('uuids', this.currentIntegrationProductUuids, this.$route.params.integrationUuid, progress);

            currentIntegrationProducts.forEach(currentIntegrationProduct => currentIntegrationProduct.destinationProducts = {});

            let internalProductUuids = currentIntegrationProducts
                .filter(currentIntegrationProduct => currentIntegrationProduct.internalProductUuid)
                .map(currentIntegrationProduct => currentIntegrationProduct.internalProductUuid);

            const promises = this.destinationIntegrations.map(async integration => {
                 let destinationProductsByUuid = {};
                 let category = this.destinationCategories[integration.uuid];
                 let categoryParams = category ? {category_uuids: [category.uuid]} : {};

                if (internalProductUuids.length > 0) {
                    let found = await this.fetchIntegrationProducts('internal_product_uuids', internalProductUuids, integration.uuid, progress, categoryParams);

                    found.forEach(integrationProduct => {
                        if (integrationProduct.internalProductUuid) {
                            destinationProductsByUuid[integrationProduct.internalProductUuid] ??= [];
                            destinationProductsByUuid[integrationProduct.internalProductUuid].push(integrationProduct);
                        };
                    });
                }

                 currentIntegrationProducts.forEach(integrationProduct => {
                    integrationProduct.destinationProducts[integration.uuid] = destinationProductsByUuid[integrationProduct.internalProductUuid] ?? [];
                 });
            });

            await Promise.all(promises);

            return currentIntegrationProducts;
        },
        async fetchIntegrationProducts(field, values, integrationUuid, {tick}, params = {}) {
            let products = [],
                requests = [];

            for (let chunkValues of chunk(values, 10)) {
                requests.push(new HttpRequest("GET", `/integrations/${integrationUuid}/products`, {
                    ...params,
                    [field]: chunkValues,
                    limit: 100
                }));
            }

            await this.httpSender.sendRequests(requests, (requests, responses) => {
                tick();
                if (responses.status !== 200) {
                    return;
                }

                products.push(...responses.body.data.map(integrationProduct => this.formatIntegrationProductData(integrationProduct)));
            });
            
            return products;
        },
        formatIntegrationProductData(integrationProduct) {
            return {
                uuid: integrationProduct.uuid,
                source_id: integrationProduct.source_id,
                second_source_id: integrationProduct.second_source_id,
                sku: integrationProduct.sku,
                names: integrationProduct.names,
                descriptions: integrationProduct.descriptions,
                seos: integrationProduct.seo,
                images: integrationProduct.images,
                group_key: integrationProduct.group_key,
                manufacturer_name: integrationProduct.manufacturer_name,
                price: integrationProduct.price,
                sale_price: integrationProduct.sale_price,
                balance: integrationProduct.balance,
                internalProductUuid: integrationProduct.product ? integrationProduct.product.uuid : null,
                type: integrationProduct.type
            };
        },
        async copyProductsToDestination() {
            let requests = [];

            this.currentIntegrationProducts.forEach(integrationProduct => {
                requests.push(...this.buildHttpRequestsForProduct(integrationProduct));
            });

            requests = requests.filter(Boolean);

            const completed = await this.copyTask.run(async ({setTotal, tick}) => {
                setTotal(requests.length);

                await this.httpSender.sendRequestsInBatches(requests, () => tick());
            });

            if (!completed) {
                return;
            }

            this.copyBadHttpHistory = this.httpSender.getHistory().filter((historyItem) => {
                return historyItem.response.status !== 201 && historyItem.response.status !== 200;
            });

            if (this.copyBadHttpHistory.length === 0) {
                new MessageNotificator().success(this.$t("alerts.success"));
                this.hide();
            }
        },
        checkAllFields(field, check) {
            this.fields.forEach(f => {
                if (field === 'create' && (this.requiredFieldsForCreate.includes(f.key))) {
                    return;
                }

                f[field] = check;
            });
        },
        getNestedValue(obj, path) {
            return path.split('.').reduce((current, key) => current?.[key], obj);
        },
        setNestedValue(obj, path, value) {
            const keys = path.split('.');
            const lastKey = keys.pop();
            const target = keys.reduce((current, key) => {
                if (!current[key]) current[key] = {};
                return current[key];
            }, obj);
            target[lastKey] = value ?? null;
        },
        buildHttpRequestsForProduct(currentIntegrationProduct) {
            let requests = [];

            for (let integration of this.destinationIntegrations) {
                const destinations = currentIntegrationProduct.destinationProducts[integration.uuid];
                const category = this.destinationCategories[integration.uuid];

                let request = null;

                if (destinations.length > 0) {
                    for (let destination of this.selectedDestinations(currentIntegrationProduct, integration)) {
                        request = new HttpRequest('PATCH', `/integrations/products/${destination.uuid}`);

                        for (let field of this.fields) {
                            const destValue = this.getNestedValue(destination, field.key);
                            const sourceValue = this.getNestedValue(currentIntegrationProduct, field.key);

                            if ((field.forceUpdate && sourceValue) || (field.update && !destValue && sourceValue)) {
                                this.setNestedValue(request.body, field.key, sourceValue);
                            }
                        }

                        if (Object.keys(request.body).length > 0) {
                            requests.push(request);
                        }
                    }
                } else if (category) {
                    request = new HttpRequest('POST', `/integrations/products`, {
                        integration_uuid: integration.uuid,
                        product_item_uuid: currentIntegrationProduct.internalProductUuid,
                        integration_category_uuid: category.uuid
                    });

                    for (let field of this.fields) {
                        const sourceValue = this.getNestedValue(currentIntegrationProduct, field.key);

                        if (field.create && sourceValue) {
                            this.setNestedValue(request.body, field.key, sourceValue);
                        }
                    }
                    requests.push(request);
                }
            }

            return requests;
        },
        setDestinationCategory(integrationUuid, category) {
            this.destinationCategories[integrationUuid] = category;
        },
        destinationTargetKey(product, integration) {
            return `${product.uuid}:${integration.uuid}`;
        },
        selectedDestinations(product, integration) {
            const destinations = product.destinationProducts[integration.uuid];

            if (destinations.length === 1) {
                return destinations;
            }

            const targetUuids = this.destinationTargets[this.destinationTargetKey(product, integration)] ?? [];

            return destinations.filter(destination => targetUuids.includes(destination.uuid));
        },
        setAllDestinationTargets(checked) {
            this.multipleDestinationRows.forEach(row => {
                this.destinationTargets[row.key] = checked ? row.destinations.map(destination => destination.uuid) : [];
            });
        },
        isDestinationTargetSelected(row, destination) {
            return (this.destinationTargets[row.key] ?? []).includes(destination.uuid);
        },
        toggleDestinationTarget(row, destination, checked) {
            const targetUuids = (this.destinationTargets[row.key] ?? []).filter(uuid => uuid !== destination.uuid);

            this.destinationTargets[row.key] = checked ? [...targetUuids, destination.uuid] : targetUuids;
        },
        publicationTitle(publication) {
            return publication.names?.uk || publication.names?.ru || publication.sku || publication.uuid;
        }
    },
    computed: {
        allFieldsSelectedForCreate() {
            return this.fields.every(field => field.create);
        },
        allFieldsSelectedForUpdate() {
            return this.fields.every(field => field.update);
        },
        allFieldsSelectedForForceUpdate() {
            return this.fields.every(field => field.forceUpdate);
        },
        everyProductHasntDestinationProduct() {
            if (!this.destinationIntegrations.length || !this.currentIntegrationProducts.length) return false;

            return this.currentIntegrationProducts.every(product =>
                this.destinationIntegrations.every(integration => product.destinationProducts[integration.uuid].length === 0)
            );
        },
        everyProductHasDestinationProduct() {
            if (!this.destinationIntegrations.length || !this.currentIntegrationProducts.length) return false;

            return this.currentIntegrationProducts.every(product =>
                this.destinationIntegrations.every(integration => product.destinationProducts[integration.uuid].length > 0)
            );
        },
        multipleDestinationRows() {
            return this.currentIntegrationProducts.flatMap(product => this.destinationIntegrations
                .filter(integration => product.destinationProducts[integration.uuid]?.length > 1)
                .map(integration => ({
                    key: this.destinationTargetKey(product, integration),
                    product,
                    integration,
                    destinations: product.destinationProducts[integration.uuid]
                }))
            );
        },
        allDestinationTargetsSelected() {
            return this.multipleDestinationRows.every(row => row.destinations.every(destination => this.isDestinationTargetSelected(row, destination)));
        },
        unresolvedDestinationRowsCount() {
            return this.multipleDestinationRows.filter(row => !this.destinationTargets[row.key]?.length).length;
        },
        someDestinationCategorySelected() {
            return this.destinationIntegrations.some(integration => this.destinationCategories[integration.uuid]);
        }
    },
    beforeUnmount() {
        this.fetchTask.cancel();
        this.copyTask.cancel();
    }
}
</script>